


import { useState } from 'react';
import styled from 'styled-components';

import { colors, fonts } from '../../shared/tokens';
import { Section, Inner, SectionHead } from '../landingShared';

const A = '/images/redesign/landing';

const PANELS = [
  {
    img: 'ind-rail.jpg',
    title: 'Rail & Transit',
    desc: 'Keep networks moving safely with real-time monitoring and control of heating and track systems across the entire rail network.',
  },
  {
    img: 'ind-freight.jpg',
    title: 'Freight & Logistics',
    desc: 'Protect assets and ensure uninterrupted cargo movement.',
  },
  {
    img: 'ind-energy.jpg',
    title: 'Energy & Infrastructure',
    desc: 'Mission-critical visibility for remote sites and substations.',
  },
  {
    img: 'ind-facilities.jpg',
    title: 'Smart Facilities',
    desc: 'Centralize environmental control at industrial scale.',
  },
];

const Industries = () => {
  const [active, setActive] = useState(0);

  return (
    <Section>
      <Inner>
        <SectionHead>
          <Heading>
            Built for Critical
            <br />
            <em>Environments</em>
          </Heading>
          <Desc>
            From freezing rail yards to offshore energy platforms — UmbrellaOS
            keeps mission-critical operations running in the harshest conditions
            on earth.
          </Desc>
        </SectionHead>

        <Accordion>
          {PANELS.map((p, i) => (
            <Panel
              key={p.title}
              $active={i === active}
              $img={`${A}/${p.img}`}
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              onClick={() => setActive(i)}
              tabIndex={0}
              aria-label={p.title}
            >
              <PanelBody>
                <PanelTitle>{p.title}</PanelTitle>
                <PanelDesc $active={i === active}>{p.desc}</PanelDesc>
              </PanelBody>
              <Accent $active={i === active} />
            </Panel>
          ))}
        </Accordion>
      </Inner>
    </Section>
  );
};

export default Industries;

const Heading = styled.h2`
  font-family: ${fonts.condensed};
  font-weight: 900;
  font-size: 52px;
  line-height: 52px;
  text-transform: uppercase;
  color: ${colors.ink};
  em {
    font-style: normal;
    color: ${colors.cyan};
  }
`;

const Desc = styled.p`
  max-width: 360px;
  padding-right: 21px;
  border-right: 1.5px solid #1e8a93;
  text-align: right;
  font-family: ${fonts.body};
  font-weight: 300;
  font-size: 15px;
  line-height: 25.5px;
  color: ${colors.muted};

  @media (max-width: 900px) {
    text-align: left;
    padding-right: 0;
    border-right: none;
    padding-left: 21px;
    border-left: 1.5px solid #1e8a93;
  }
`;

const Accordion = styled.div`
  margin-top: 56px;
  display: flex;
  gap: 6px;
  height: 480px;
  border-radius: 32px;
  overflow: hidden;

  @media (max-width: 760px) {
    flex-direction: column;
    height: auto;
    border-radius: 20px;
  }
`;




const Panel = styled.div`
  position: relative;
  flex: ${({ $active }) => ($active ? '2.6' : '1')} 1 0;
  min-width: 0;
  overflow: hidden;
  cursor: pointer;
  outline: none;
  background-image: url(${({ $img }) => $img});
  background-size: cover;
  background-position: center;
  transition: flex-grow 0.65s cubic-bezier(0.22, 1, 0.36, 1);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to top,
      rgba(5, 8, 15, 0.95) 0%,
      rgba(5, 8, 15, 0.4) 50%,
      rgba(5, 8, 15, 0.1) 100%
    );
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px ${colors.cyanBorder};
  }

  @media (max-width: 760px) {
    flex: none;
    height: 240px;
    transition: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const PanelBody = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  padding: 32px;
`;

const PanelTitle = styled.h3`
  font-family: ${fonts.condensed};
  font-weight: 800;
  font-size: 24px;
  line-height: 26px;
  text-transform: uppercase;
  color: ${colors.ink};
`;

const PanelDesc = styled.p`
  margin-top: 8px;
  max-width: 300px;
  font-family: ${fonts.body};
  font-weight: 300;
  font-size: 14px;
  line-height: 21px;
  color: rgba(255, 255, 255, 0.7);

  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transform: translateY(${({ $active }) => ($active ? '0' : '10px')});
  transition: opacity 0.4s ease 0.1s, transform 0.4s ease 0.1s;

  @media (max-width: 760px) {
    opacity: 1;
    transform: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;


const Accent = styled.span`
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 2;
  height: 3px;
  width: 100%;
  background: ${colors.cyan};
  transform: scaleX(${({ $active }) => ($active ? 1 : 0)});
  transform-origin: left;
  transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
